import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../services/firebase";
import {
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import PhotoUploadBase64 from "../../components/PhotoUploadBase64";
import PrestartModal from "../../components/PrestartModal";

import { jobTypeUi } from "../../services/jobTypes";
import DocketSignModal from "../../components/DocketSignModal";
import { FUTILE_CHARGE_AMOUNT, inferCardFromJobType, getJobDocuments } from "./driverJobList/helpers";
import FutileModal from "./driverJobList/FutileModal";
import SkipModal from "./driverJobList/SkipModal";
import InlineJobNotes from "./driverJobList/InlineJobNotes";
import RunDocketSection from "./driverJobList/RunDocketSection";

/* ---------------- Main component ---------------- */
export default function DriverJobList({
  assignment,
  date,
  user,
  handleTimestamp,
  refresh,
  onOpenJob,
  assignmentDocId,
  runDocMap,
}) {
  const navigate = useNavigate();

  const [prestartById, setPrestartById] = useState({});
  const [showPrestart, setShowPrestart] = useState(false);
  const [activePrestartCtx, setActivePrestartCtx] = useState(null);
  const [skipOpen, setSkipOpen] = useState(false);
  const [skipTarget, setSkipTarget] = useState(null);
  const [futileOpen, setFutileOpen] = useState(false);
  const [futileTarget, setFutileTarget] = useState(null);
  const [jobMeta, setJobMeta] = useState({});
  const [activeDocketJob, setActiveDocketJob] = useState(null);

  const companyId = assignment?.companyId || "";
  const runMeta = useMemo(() => assignment?.runMeta || {}, [assignment]);
  const runs = useMemo(() => assignment?.runs || {}, [assignment]);

  const getRunNumber = (label = "") => {
    const m = String(label).match(/(\d+)/);
    return m ? parseInt(m[1], 10) : 999999;
  };

  const runEntries = Object.entries(runs).sort(
    ([a], [b]) => getRunNumber(a) - getRunNumber(b),
  );

  const uniqueRunCompanyIds = useMemo(() => {
    const set = new Set();

    Object.keys(runs || {}).forEach((rk) => {
      const m = runMeta?.[rk];
      const cid = m?.companyId;
      if (cid && cid !== "mixed") set.add(cid);
    });

    if (!set.size) {
      Object.values(runs || {}).forEach((list) => {
        (Array.isArray(list) ? list : []).forEach((s) => {
          const cid = s?.companyId;
          if (cid && cid !== "mixed") set.add(cid);
        });
      });
    }

    return Array.from(set);
  }, [runs, runMeta]);

  const isMultiCompanyDay = uniqueRunCompanyIds.length > 1;

  const inferredSingleCompanyId = !isMultiCompanyDay
    ? uniqueRunCompanyIds[0] || ""
    : "";

  const inferredSingleCompanyName = useMemo(() => {
    if (isMultiCompanyDay || !inferredSingleCompanyId) return "";

    for (const rk of Object.keys(runs || {})) {
      const m = runMeta?.[rk];
      if (m?.companyId === inferredSingleCompanyId && m?.companyName) {
        return m.companyName;
      }
    }

    for (const list of Object.values(runs || {})) {
      for (const s of Array.isArray(list) ? list : []) {
        if (s?.companyId === inferredSingleCompanyId && s?.companyName) {
          return s.companyName;
        }
      }
    }

    return "";
  }, [isMultiCompanyDay, inferredSingleCompanyId, runs, runMeta]);

  const legacyPrestartDocId = user?.uid && date ? `${user.uid}_${date}` : "";

  const safeRunKey = (value = "") =>
    String(value || "")
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[^a-zA-Z0-9_-]/g, "");

  const getRunContext = (runKey, firstStop = null) => {
    const meta = runMeta?.[runKey] || {};
    const stop =
      firstStop ||
      (Array.isArray(runs?.[runKey]) ? runs[runKey][0] : null) ||
      {};

    const uiVariant = String(
      meta?.uiVariant || stop?.uiVariant || "",
    ).toLowerCase();
    const isGreenspace = uiVariant === "greenspace";
    const companyIdForRun =
      meta?.companyId || stop?.companyId || companyId || "";
    const companyNameForRun =
      meta?.companyName ||
      stop?.companyName ||
      (companyIdForRun && companyIdForRun !== "mixed" ? companyIdForRun : "");
    const vehicleId = meta?.vehicleId || stop?.vehicleId || "";
    const vehicleRego =
      meta?.vehicleRego ||
      meta?.rego ||
      meta?.truckRego ||
      stop?.vehicleRego ||
      stop?.rego ||
      stop?.truckRego ||
      "";
    const vehicleLoadType =
      meta?.vehicleLoadType ||
      meta?.loadType ||
      meta?.unitType ||
      meta?.truckType ||
      stop?.vehicleLoadType ||
      stop?.loadType ||
      stop?.unitType ||
      stop?.truckType ||
      "";
    const vehicleLabel =
      meta?.vehicleLabel ||
      meta?.truckLabel ||
      stop?.vehicleLabel ||
      stop?.truckLabel ||
      [vehicleRego, vehicleLoadType].filter(Boolean).join(" • ");

    const hasVehicle = !!(vehicleId || vehicleRego || vehicleLoadType || vehicleLabel);

    const prestartDocId =
      (isGreenspace || hasVehicle) && user?.uid && date
        ? `${user.uid}_${date}_${safeRunKey(runKey)}`
        : companyIdForRun
          ? `${companyIdForRun}_${user.uid}_${date}`
          : legacyPrestartDocId;

    return {
      isGreenspace,
      companyId: companyIdForRun,
      companyName: companyNameForRun,
      vehicleId,
      vehicleRego,
      vehicleLoadType,
      vehicleLabel,
      hasVehicle,
      prestartDocId,
    };
  };

  const isPrestartCompleted = (d) => {
    if (!d) return false;
    const ts = (v) =>
      v && typeof v === "object" && typeof v._seconds === "number"
        ? v._seconds * 1000
        : v;
    const candidates = [
      d.completedAt,
      d.submittedAt,
      d.finishedAt,
      d.doneAt,
      d.completedTime,
      d.submittedTime,
      d.completed,
      d.submitted,
      d.isCompleted,
      d.isSubmitted,
    ].map(ts);
    return candidates.some((v) => !!v);
  };

  const legacyPrestartDone = isPrestartCompleted(
    prestartById?.[legacyPrestartDocId],
  );

  const isPrestartDoneForCompany = (cid) => {
    if (!cid || !user?.uid || !date) return legacyPrestartDone;
    const id = `${cid}_${user.uid}_${date}`;
    return isPrestartCompleted(prestartById?.[id]);
  };

  const isPrestartDoneForRun = (runKey) => {
    const ctx = getRunContext(runKey);
    return isPrestartCompleted(prestartById?.[ctx.prestartDocId]);
  };

  async function ensurePrestartStart(prestartDocId, cid, cName, extra = {}) {
    if (!user?.uid || !date || !prestartDocId) return;

    try {
      const ref = doc(db, "prestarts", prestartDocId);
      const snap = await getDoc(ref);
      const d = snap.exists() ? snap.data() : {};
      const alreadyStarted =
        d?.openedAt ||
        d?.createdAt ||
        d?.startedAt ||
        d?.startAt ||
        d?.started ||
        d?.startTime ||
        d?.startedTime;

      if (!alreadyStarted) {
        await setDoc(
          ref,
          {
            driverId: user.uid,
            email: user?.email || "",
            date,
            companyId: cid || companyId || "",
            companyName: cName || "",
            runKey: extra.runKey || "",
            vehicleId: extra.vehicleId || "",
            vehicleRego: extra.vehicleRego || "",
            vehicleLoadType: extra.vehicleLoadType || "",
            vehicleLabel: extra.vehicleLabel || "",
            openedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );
      }
    } catch (e) {
      console.error("Failed to set prestart start time:", e);
    }
  }

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        if (!user?.uid || !date) return;

        const ids = new Set();
        ids.add(`${user.uid}_${date}`);

        uniqueRunCompanyIds.forEach((cid) =>
          ids.add(`${cid}_${user.uid}_${date}`),
        );
        Object.keys(runs || {}).forEach((runKey) => {
          const ctx = getRunContext(runKey);
          if (ctx.prestartDocId) ids.add(ctx.prestartDocId);
        });

        const entries = await Promise.all(
          Array.from(ids).map(async (id) => {
            try {
              const snap = await getDoc(doc(db, "prestarts", id));
              return [id, snap.exists() ? snap.data() : null];
            } catch {
              return [id, null];
            }
          }),
        );

        if (!mounted) return;

        const next = {};
        entries.forEach(([id, data]) => {
          if (data) next[id] = data;
        });

        setPrestartById(next);
      } catch (e) {
        console.error("prestart load failed:", e);
      }
    })();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getRunContext/runs are recreated every render; scoping to the primitive identifiers that actually change avoids refetching prestart docs on every render
  }, [user?.uid, date, uniqueRunCompanyIds]);

  useEffect(() => {
    const runs = assignment?.runs;
    if (!runs) return;
    const ids = new Set();
    Object.entries(runs).forEach(([, stops]) => {
      (stops || []).forEach((s) => {
        if (!s) return;
        if (s.jobId && !jobMeta[s.jobId]) ids.add(s.jobId);
      });
    });
    if (!ids.size) return;

    (async () => {
      const entries = await Promise.all(
        Array.from(ids).map(async (id) => {
          try {
            const snap = await getDoc(doc(db, "jobs", id));
            if (!snap.exists()) return [id, null];
            const d = snap.data() || {};
            const code = (d.jobTypeCode || "").toString().toUpperCase() || "DV";
            const name = d.jobTypeName || jobTypeUi(code).label;
            return [
              id,
              {
                jobTypeName: name,
                code,
                assignedSource: d.assignedSource || "",
                docketUrl: d.docketUrl || "",
                docketStoragePath: d.docketStoragePath || "",
                docketFileName: d.docketFileName || "",
                docketContentType: d.docketContentType || "",
                docketMode: d.docketMode === "view" ? "view" : "sign",
                jobDocuments: getJobDocuments(d),
                signedJobDocuments: d.signedJobDocuments || {},
                signedDocketUrl: d.signedDocketUrl || "",
                signedDocketStoragePath: d.signedDocketStoragePath || "",
                signedDocketFileName: d.signedDocketFileName || "",
                signedDocketContentType: d.signedDocketContentType || "",
              },
            ];
          } catch {
            return [id, null];
          }
        }),
      );
      setJobMeta((prev) => {
        const next = { ...prev };
        entries.forEach(([id, meta]) => meta && (next[id] = meta));
        return next;
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deliberately excludes jobMeta: including it would re-trigger this effect on every setJobMeta call, retrying any job whose metadata fetch failed in an infinite loop
  }, [assignment?.runs]);

  function updateJobMeta(jobId, patch) {
    if (!jobId) return;
    setJobMeta((prev) => ({
      ...prev,
      [jobId]: {
        ...(prev[jobId] || {}),
        ...patch,
      },
    }));
  }

  async function updateJob(jobId, patch) {
    if (!jobId) return;
    try {
      const ref = doc(db, "jobs", jobId);
      await updateDoc(ref, { ...patch, updatedAt: serverTimestamp() });
    } catch (e) {
      console.error("job update failed:", e);
    }
  }

  async function updateAssignmentRuns(nextRuns) {
    if (!user?.uid || !date) return;

    try {
      const fallbackDocId = assignmentDocId || `${user.uid}_${date}`;
      const groupedRuns = {};

      Object.entries(nextRuns || {}).forEach(([runKey, runStops]) => {
        const targetDocId = runDocMap?.[runKey] || fallbackDocId;
        if (!groupedRuns[targetDocId]) groupedRuns[targetDocId] = {};
        groupedRuns[targetDocId][runKey] = runStops;
      });

      const writes = Object.entries(groupedRuns).map(
        async ([targetDocId, runsForDoc]) => {
          await setDoc(
            doc(db, "assignments", targetDocId),
            {
              driverId: user.uid,
              driverEmail: user.email || "",
              date,
              runs: runsForDoc,
              updatedAt: Date.now(),
            },
            { merge: true },
          );
        },
      );

      await Promise.all(writes);
      refresh?.();
    } catch (e) {
      console.error("assignment update failed:", e);
    }
  }

  useEffect(() => {
    const currentRuns = assignment?.runs;
    if (!user?.uid || !date || !currentRuns) return;

    let changed = false;
    const updatedRuns = {};

    for (const [runKey, list] of Object.entries(currentRuns)) {
      const addresses = Array.isArray(list) ? [...list] : [];
      const hasEmpty = addresses.some((s) => {
        const code = inferCardFromJobType(s?.jobTypeCode, s?.jobTypeName);
        const name = String(s?.jobTypeName || "").toLowerCase();
        const rawCode = String(s?.jobTypeCode || "").toLowerCase();
        return (
          code === "empty" ||
          name.includes("empty") ||
          rawCode.includes("empty")
        );
      });
      const docketIndex = addresses.findIndex((a) => a?.isWeighbridgeDocket);

      if (hasEmpty && docketIndex === -1) {
        addresses.push({
          isWeighbridgeDocket: true,
          jobTypeCode: "DOCKET",
          proofPhotos: [],
        });
        changed = true;
      } else if (!hasEmpty && docketIndex !== -1) {
        addresses.splice(docketIndex, 1);
        changed = true;
      }

      updatedRuns[runKey] = addresses;
    }

    if (changed) {
      updateAssignmentRuns(updatedRuns);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- updateAssignmentRuns is recreated every render; assignment?.runs already covers the data this effect reacts to
  }, [assignment?.runs, user?.uid, date, companyId]);

  async function applySkip(runKey, idx, addr, reason, note) {
    const currentRuns = assignment?.runs || {};
    const nextRuns = JSON.parse(JSON.stringify(currentRuns));
    Object.assign(nextRuns[runKey][idx], {
      skipped: true,
      skipReason: reason,
      skipNote: note || "",
      skippedAt: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
      skippedById: user?.uid || "",
      skippedByEmail: user?.email || "",
    });
    await updateAssignmentRuns(nextRuns);
    if (addr?.jobId) {
      await updateJob(addr.jobId, {
        status: "skipped",
        skippedAt: Date.now(),
        skipReason: reason,
        skipNote: note || "",
        skippedById: user?.uid || "",
        skippedByEmail: user?.email || "",
      });
    }
  }

  async function applyFutile(runKey, idx, addr, note) {
    const currentRuns = assignment?.runs || {};
    const nextRuns = JSON.parse(JSON.stringify(currentRuns));
    const markedAt = new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

    Object.assign(nextRuns[runKey][idx], {
      skipped: true,
      futile: true,
      futileNote: note || "",
      futileCharge: FUTILE_CHARGE_AMOUNT,
      futileAmount: FUTILE_CHARGE_AMOUNT,
      futileAt: markedAt,
      futileById: user?.uid || "",
      futileByEmail: user?.email || "",
      skipReason: "",
      skipNote: note || "",
      skippedAt: markedAt,
      skippedById: user?.uid || "",
      skippedByEmail: user?.email || "",
    });

    await updateAssignmentRuns(nextRuns);
    if (addr?.jobId) {
      await updateJob(addr.jobId, {
        status: "futile",
        futile: true,
        futileNote: note || "",
        futileCharge: FUTILE_CHARGE_AMOUNT,
        futileAmount: FUTILE_CHARGE_AMOUNT,
        futileAt: Date.now(),
        futileById: user?.uid || "",
        futileByEmail: user?.email || "",
        skippedAt: Date.now(),
        skipReason: "",
        skipNote: note || "",
        skippedById: user?.uid || "",
        skippedByEmail: user?.email || "",
      });
    }
  }

  async function undoSkip(runKey, idx, addr) {
    const currentRuns = assignment?.runs || {};
    const nextRuns = JSON.parse(JSON.stringify(currentRuns));
    delete nextRuns[runKey][idx].skipped;
    delete nextRuns[runKey][idx].futile;
    delete nextRuns[runKey][idx].futileNote;
    delete nextRuns[runKey][idx].futileCharge;
    delete nextRuns[runKey][idx].futileAmount;
    delete nextRuns[runKey][idx].futileAt;
    delete nextRuns[runKey][idx].futileById;
    delete nextRuns[runKey][idx].futileByEmail;
    delete nextRuns[runKey][idx].skipReason;
    delete nextRuns[runKey][idx].skipNote;
    delete nextRuns[runKey][idx].skippedAt;
    delete nextRuns[runKey][idx].skippedById;
    delete nextRuns[runKey][idx].skippedByEmail;
    await updateAssignmentRuns(nextRuns);
    if (addr?.jobId) {
      await updateJob(addr.jobId, {
        status: "assigned",
        futile: false,
        futileNote: "",
        futileCharge: 0,
        futileAmount: 0,
        futileAt: null,
        futileById: "",
        futileByEmail: "",
        skipReason: "",
        skipNote: "",
        skippedAt: null,
        skippedById: "",
        skippedByEmail: "",
      });
    }
  }

  useEffect(() => {
    try {
      const jobId =
        typeof window !== "undefined"
          ? localStorage.getItem("autoFinishJobId")
          : null;
      const runs = assignment?.runs;
      if (!jobId || !runs) return;

      let target = null;
      for (const [runKey, addresses] of Object.entries(runs)) {
        const idx = (addresses || []).findIndex((a) => a && a.jobId === jobId);
        if (idx !== -1) {
          target = { runKey, idx, addr: addresses[idx] };
          break;
        }
      }
      if (!target) {
        localStorage.removeItem("autoFinishJobId");
        return;
      }

      (async () => {
        const { runKey, idx, addr } = target;

        const ctx = getRunContext(runKey, addr);
        const mustBeDone = ctx.isGreenspace
          ? isPrestartDoneForRun(runKey)
          : isMultiCompanyDay
            ? isPrestartDoneForCompany(ctx.companyId)
            : legacyPrestartDone;

        if (!mustBeDone) {
          localStorage.removeItem("autoFinishJobId");
          return;
        }

        if (!addr.startTripAt) {
          handleTimestamp(runKey, idx, "startTripAt");
          if (addr.jobId)
            await updateJob(addr.jobId, {
              status: "enRoute",
              enRouteAt: Date.now(),
            });
        }
        if (!addr.startJobAt) {
          handleTimestamp(runKey, idx, "startJobAt");
          if (addr.jobId)
            await updateJob(addr.jobId, {
              status: "arrived",
              arrivedAt: Date.now(),
            });
        }
        if (!addr.completedTime) {
          handleTimestamp(runKey, idx, "completedTime");
          if (addr.jobId)
            await updateJob(addr.jobId, {
              status: "completed",
              completedAt: Date.now(),
            });
        }

        try {
          localStorage.removeItem("autoFinishJobId");
        } catch {}
        refresh?.();
      })();
    } catch (e) {
      console.warn("autoFinishJobId handling error", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getRunContext/handleTimestamp/isPrestartDoneForCompany/isPrestartDoneForRun/refresh are recreated every render; this effect self-guards via the one-shot autoFinishJobId localStorage flag, so re-running it on every render would just be a redundant no-op read
  }, [
    assignment?.runs,
    legacyPrestartDone,
    isMultiCompanyDay,
    prestartById,
    runMeta,
  ]);

  if (!runEntries.length)
    return (
      <p className="text-sm text-gray-500 py-4">
        No jobs assigned for this run.
      </p>
    );

  const hasAnyGreenspaceRun = runEntries.some(
    ([runKey, addresses]) =>
      getRunContext(runKey, Array.isArray(addresses) ? addresses[0] : null)
        .isGreenspace,
  );
  const showSinglePrestartBanner =
    !hasAnyGreenspaceRun && !isMultiCompanyDay && !legacyPrestartDone;

  async function openLegacyPrestart() {
    const id = `${user.uid}_${date}`;
    if (legacyPrestartDone) return;

    const cid = inferredSingleCompanyId || companyId || "";
    const cName = inferredSingleCompanyName || "";

    await ensurePrestartStart(id, cid, cName);
    setActivePrestartCtx({
      prestartDocId: id,
      companyId: cid,
      companyName: cName,
    });
    setShowPrestart(true);
  }

  async function openRunPrestart(runKey, cid, cName) {
    const ctx = getRunContext(runKey);
    const alreadyDone = ctx.isGreenspace
      ? isPrestartDoneForRun(runKey)
      : cid
        ? isPrestartDoneForCompany(cid)
        : legacyPrestartDone;
    if (alreadyDone) return;
    await ensurePrestartStart(
      ctx.prestartDocId,
      ctx.companyId || cid || companyId,
      ctx.companyName || cName || "",
      {
        runKey,
        vehicleId: ctx.vehicleId,
        vehicleRego: ctx.vehicleRego,
        vehicleLoadType: ctx.vehicleLoadType,
        vehicleLabel: ctx.vehicleLabel,
      },
    );
    setActivePrestartCtx({
      prestartDocId: ctx.prestartDocId,
      companyId: ctx.companyId || cid || companyId,
      companyName: ctx.companyName || cName || "",
      runKey,
      vehicleId: ctx.vehicleId,
      vehicleRego: ctx.vehicleRego,
      vehicleLoadType: ctx.vehicleLoadType,
      vehicleLabel: ctx.vehicleLabel,
    });
    setShowPrestart(true);
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {showSinglePrestartBanner && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-xs sm:text-sm text-amber-900 flex flex-wrap items-center justify-between gap-2">
          <span>
            Please complete the Daily Pre-Start before starting your first trip.
          </span>
          <button
            onClick={openLegacyPrestart}
            className="px-3 py-1.5 rounded-lg bg-gray-900 text-white text-xs font-medium hover:bg-black transition"
          >
            Open pre-start form
          </button>
        </div>
      )}

      {showPrestart && activePrestartCtx && (
        <PrestartModal
          driverId={user.uid}
          date={date}
          driverEmail={user?.email || ""}
          prestartDocId={activePrestartCtx.prestartDocId}
          companyId={activePrestartCtx.companyId || undefined}
          companyName={activePrestartCtx.companyName || undefined}
          runKey={activePrestartCtx.runKey || ""}
          vehicleId={activePrestartCtx.vehicleId || ""}
          vehicleRego={activePrestartCtx.vehicleRego || ""}
          vehicleLoadType={activePrestartCtx.vehicleLoadType || ""}
          vehicleLabel={activePrestartCtx.vehicleLabel || ""}
          onClose={() => {
            setShowPrestart(false);
            setActivePrestartCtx(null);
          }}
          onCompleted={(data) => {
            setPrestartById((prev) => ({
              ...prev,
              [activePrestartCtx.prestartDocId]: data || {
                completedAt: Date.now(),
              },
            }));
            setShowPrestart(false);
            setActivePrestartCtx(null);
          }}
        />
      )}

      {runEntries.map(([runKey, addresses]) => {
        const list = Array.isArray(addresses) ? addresses : [];

        const ctx = getRunContext(runKey, list?.[0] || null);
        const runCompanyId = ctx.companyId;
        const runCompanyName = ctx.companyName;

        const runPrestartDone = ctx.isGreenspace
          ? isPrestartDoneForRun(runKey)
          : isMultiCompanyDay
            ? isPrestartDoneForCompany(runCompanyId)
            : legacyPrestartDone;

        const docketIndex = list.findIndex((a) => a?.isWeighbridgeDocket);
        const docketAddr =
          docketIndex >= 0 && list[docketIndex] ? list[docketIndex] : null;

        const nonDocketList = list.filter((a) => !a?.isWeighbridgeDocket);

        return (
          <section
            key={runKey}
            className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden"
          >
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-3">
              <div className="flex flex-col">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
                  {runKey}
                </h3>
                {(isMultiCompanyDay || ctx.isGreenspace) && (
                  <>
                    <div className="text-xs text-gray-500">
                      Company:{" "}
                      <span className="font-medium text-gray-700">
                        {runCompanyName || "—"}
                      </span>
                    </div>
                    {ctx.vehicleLabel && (
                      <div className="text-xs text-gray-500">
                        Vehicle:{" "}
                        <span className="font-medium text-gray-700">
                          {ctx.vehicleLabel}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>

              {(ctx.isGreenspace || isMultiCompanyDay) && !runPrestartDone && (
                <button
                  onClick={() =>
                    openRunPrestart(runKey, runCompanyId, runCompanyName)
                  }
                  className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-medium hover:bg-amber-700 transition"
                >
                  Pre-start required
                </button>
              )}
            </div>

            {(ctx.isGreenspace || isMultiCompanyDay) && !runPrestartDone && (
              <div className="border-b border-amber-200 bg-amber-50 px-4 py-3 text-xs sm:text-sm text-amber-900 flex flex-wrap items-center justify-between gap-2">
                <span>
                  Please complete the Pre-Start for{" "}
                  <span className="font-semibold">
                    {ctx.isGreenspace
                      ? ctx.vehicleLabel || runCompanyName || "this run"
                      : runCompanyName || "this company"}
                  </span>{" "}
                  before starting this run.
                </span>
                <button
                  onClick={() =>
                    openRunPrestart(runKey, runCompanyId, runCompanyName)
                  }
                  className="px-3 py-1.5 rounded-lg bg-gray-900 text-white text-xs font-medium hover:bg-black transition"
                >
                  Open pre-start form
                </button>
              </div>
            )}

            <div className="p-3 sm:p-4 space-y-4">
              {list.map((addr, idx) => {
                if (!addr || addr.isWeighbridgeDocket) return null;

                const hasStartTrip = !!addr.startTripAt;
                const hasStartJob = !!addr.startJobAt;
                const hasFinished = !!addr.completedTime;
                const isSkipped = !!addr.skipped;
                const isFutile = !!addr.futile;

                const currentIndex = nonDocketList.indexOf(addr);
                const prevComplete =
                  currentIndex <= 0
                    ? true
                    : nonDocketList
                        .slice(0, currentIndex)
                        .every((it) => !!it.completedTime || !!it.skipped);

                const canAct = prevComplete && !hasFinished && !isSkipped;

                const siteLabel = addr.siteLabel || "";
                const siteAccessNotes =
                  addr.siteAccessNotes || addr.accessNotes || "";
                const epaWasteTracker = addr.epaWasteTracker || "";

                const ui = jobTypeUi(addr.jobTypeCode);
                const card = inferCardFromJobType(
                  addr.jobTypeCode,
                  addr.jobTypeName || ui?.label,
                );
                const jobTypeDisplay = ui?.label || "Job";
                const jobDocuments = jobMeta[addr.jobId]?.jobDocuments || [];
                const signedJobDocuments =
                  jobMeta[addr.jobId]?.signedJobDocuments || {};
                const signableDocumentCount = jobDocuments.filter(
                  (item) => item.mode === "sign",
                ).length;
                const signedDocumentCount = jobDocuments.filter(
                  (item) =>
                    item.mode === "sign" &&
                    (item.id === "legacy"
                      ? jobMeta[addr.jobId]?.signedDocketUrl
                      : signedJobDocuments[item.id]?.url),
                ).length;
                const normalizedCard = String(card || "").toLowerCase();
                const normalizedJobType = String(
                  jobTypeDisplay || "",
                ).toLowerCase();
                const normalizedJobCode = String(
                  addr.jobTypeCode || "",
                ).toLowerCase();
                const isEmptyJob =
                  normalizedCard === "empty" ||
                  normalizedJobCode === "empty" ||
                  normalizedJobCode.includes("empty") ||
                  normalizedJobType.includes("empty");
                const showExtraWasteTypeButton = !isEmptyJob;

                let openJobLabel = "Open job";

                if (normalizedJobType.includes("empty")) {
                  openJobLabel = "Waste Type";
                } else if (
                  normalizedJobType.includes("pick up") ||
                  normalizedJobType.includes("pickup")
                ) {
                  openJobLabel = "Open Pick Up";
                } else if (normalizedJobType.includes("deliver")) {
                  openJobLabel = `Open ${jobTypeDisplay}`;
                } else if (jobTypeDisplay && jobTypeDisplay !== "Job") {
                  openJobLabel = `Open ${jobTypeDisplay}`;
                }

                async function openCard(targetCard) {
                  if (!addr?.jobId) return;

                  const navState = targetCard
                    ? {
                        forcedCard: targetCard,
                        wasteTypeMode: targetCard === "empty",
                      }
                    : null;

                  if (typeof onOpenJob === "function") {
                    onOpenJob(addr.jobId, runKey, targetCard || null, navState);
                    return;
                  }

                  if (targetCard)
                    navigate(`/job/${addr.jobId}?card=${targetCard}`, {
                      state: navState,
                    });
                  else navigate(`/job/${addr.jobId}`);
                }

                async function openThisJob() {
                  await openCard(card);
                }

                async function openWasteTypeCard() {
                  await openCard("empty");
                }

                return (
                  <div
                    key={idx}
                    className={`rounded-xl border shadow-sm ${
                      hasFinished
                        ? "bg-emerald-50 border-emerald-200"
                        : isSkipped
                          ? "bg-amber-50 border-amber-200"
                          : "bg-white border-gray-200"
                    }`}
                  >
                    <div className="p-3 sm:p-4 flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h4 className="font-semibold text-gray-900 text-base sm:text-lg break-words">
                          {addr.address || "No address"}
                        </h4>

                        {(siteLabel || addr.customerName || addr.eta) && (
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] sm:text-xs">
                            {siteLabel && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 border border-gray-200 text-gray-700">
                                Site: {siteLabel}
                              </span>
                            )}
                            {addr.customerName && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 border border-gray-200 text-gray-700">
                                Customer: {addr.customerName}
                              </span>
                            )}
                            {addr.eta && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 border border-gray-200 text-gray-700">
                                ETA: {addr.eta}
                              </span>
                            )}
                          </div>
                        )}

                        {siteAccessNotes && (
                          <div className="mt-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
                            <div className="text-[11px] sm:text-xs font-semibold text-blue-900">
                              Site access notes
                            </div>
                            <div className="mt-1 text-sm text-blue-900 whitespace-pre-wrap">
                              {siteAccessNotes}
                            </div>
                          </div>
                        )}

                        {addr.description && (
                          <div className="mt-2 p-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-800 whitespace-pre-wrap">
                            {addr.description}
                          </div>
                        )}

                        {epaWasteTracker && (
                          <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                            <div className="text-[11px] sm:text-xs font-semibold text-emerald-900">
                              EPA Waste Tracker
                            </div>
                            <div className="mt-1 text-sm font-medium text-emerald-900 break-words">
                              {epaWasteTracker}
                            </div>
                          </div>
                        )}

                        {(addr.siteContact || addr.sitePhone) && (
                          <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
                            {addr.siteContact && (
                              <div className="text-gray-800">
                                <span className="text-gray-500 text-xs">
                                  Contact:{" "}
                                </span>
                                <span className="font-medium">
                                  {addr.siteContact}
                                </span>
                              </div>
                            )}

                            {addr.sitePhone && (
                              <div className="mt-1 flex items-center gap-2">
                                <span className="text-gray-500 text-xs">
                                  Phone:{" "}
                                </span>

                                <a
                                  href={`tel:${addr.sitePhone}`}
                                  className="font-medium text-emerald-700 hover:underline"
                                >
                                  {addr.sitePhone}
                                </a>

                                <button
                                  onClick={() =>
                                    navigator.clipboard.writeText(
                                      addr.sitePhone,
                                    )
                                  }
                                  className="text-[10px] text-gray-500 hover:text-gray-800"
                                >
                                  Copy
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        <div className="mt-2 text-[11px] sm:text-xs text-gray-800 flex flex-wrap gap-2 items-center">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 border border-gray-200">
                            Job type:{" "}
                            <span className="font-semibold ml-1">
                              {jobTypeDisplay}
                            </span>
                          </span>
                          <span className="italic text-gray-600">
                            {isEmptyJob
                              ? `Open the ${jobTypeDisplay} card to enter job details.`
                              : `Open the ${jobTypeDisplay} card to enter job details. Use Waste Type as well if waste is collected on this stop.`}
                          </span>
                        </div>

                        {addr?.jobId && jobDocuments.length > 0 && (
                          <p className="mt-2 inline-flex items-center px-2 py-1 rounded-lg bg-blue-50 border border-blue-200 text-[11px] text-blue-800">
                            {jobDocuments.length} job document
                            {jobDocuments.length === 1 ? "" : "s"}
                            {signableDocumentCount > 0
                              ? ` - ${signedDocumentCount} of ${signableDocumentCount} signed`
                              : " - view only"}
                          </p>
                        )}

                        {!canAct && !hasFinished && !isSkipped && (
                          <p className="mt-1 inline-flex items-center px-2 py-1 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-800">
                            Complete earlier stops first.
                          </p>
                        )}
                        {isSkipped && (
                          <p className="mt-1 inline-flex items-center px-2 py-1 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-800">
                            {isFutile ? `FUTILE - $${FUTILE_CHARGE_AMOUNT}` : "Skipped"}
                            {addr.skipReason ? ` – ${addr.skipReason}` : ""}
                            {addr.skippedByEmail
                              ? ` (by ${addr.skippedByEmail})`
                              : ""}
                          </p>
                        )}
                        {isFutile && addr.futileNote && (
                          <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900 whitespace-pre-wrap">
                            <span className="font-semibold">Futile note:</span>{" "}
                            {addr.futileNote}
                          </div>
                        )}
                      </div>

                      <div className="flex-shrink-0 flex flex-col sm:flex-row gap-2">
                        <a
                          href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                            addr.address || "",
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-gray-900 hover:bg-black text-white px-3 py-2 rounded-lg text-xs sm:text-sm text-center transition"
                        >
                          Navigate
                        </a>

                        <button
                          onClick={openThisJob}
                          disabled={!addr?.jobId}
                          title={
                            addr?.jobId
                              ? openJobLabel
                              : "No job created for this stop"
                          }
                          className={`px-3 py-2 rounded-lg text-xs sm:text-sm whitespace-nowrap transition ${
                            addr?.jobId
                              ? "bg-white border border-gray-300 text-gray-900 hover:bg-gray-50"
                              : "bg-gray-200 text-gray-500 cursor-not-allowed"
                          }`}
                        >
                          {openJobLabel}
                        </button>

                        {showExtraWasteTypeButton && (
                          <button
                            onClick={openWasteTypeCard}
                            disabled={!addr?.jobId}
                            title={
                              addr?.jobId
                                ? "Waste Type"
                                : "No job created for this stop"
                            }
                            className={`px-3 py-2 rounded-lg text-xs sm:text-sm whitespace-nowrap transition ${
                              addr?.jobId
                                ? "bg-emerald-600 text-white hover:bg-emerald-700"
                                : "bg-gray-200 text-gray-500 cursor-not-allowed"
                            }`}
                          >
                            Waste Type
                          </button>
                        )}

                        {addr?.jobId && jobDocuments.length > 0 && (
                          <button
                            type="button"
                            onClick={() =>
                              setActiveDocketJob({
                                jobId: addr.jobId,
                                documents: jobDocuments,
                                documentIndex: 0,
                              })
                            }
                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-xs sm:text-sm text-center transition"
                          >
                            Open documents ({jobDocuments.length})
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Progress: Start trip / Job start / Finish job as a 3-segment stepper.
                        Only one segment is ever actually actionable at a time (mirrors the
                        hasStartTrip/hasStartJob/hasFinished gating below), so it's shown as a
                        stepper instead of three separately-styled full-width buttons. */}
                    <div className="px-3 sm:px-4 pb-2">
                      <div className="flex gap-2">
                        {hasStartTrip ? (
                          <div className="flex-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-2 text-center">
                            <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                              Start trip
                            </div>
                            <div className="text-xs font-semibold text-emerald-700">
                              ✓ {addr.startTripAt}
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={async () => {
                              const mustBeDone = ctx.isGreenspace
                                ? runPrestartDone
                                : isMultiCompanyDay
                                  ? runPrestartDone
                                  : legacyPrestartDone;

                              if (!mustBeDone && !hasStartTrip) {
                                if (ctx.isGreenspace || isMultiCompanyDay) {
                                  await openRunPrestart(
                                    runKey,
                                    runCompanyId,
                                    runCompanyName,
                                  );
                                } else {
                                  await openLegacyPrestart();
                                }
                                return;
                              }

                              if (!hasStartTrip && canAct) {
                                handleTimestamp(runKey, idx, "startTripAt");
                                if (addr.jobId)
                                  updateJob(addr.jobId, {
                                    status: "enRoute",
                                    enRouteAt: Date.now(),
                                  });
                              }
                            }}
                            disabled={!canAct}
                            className={`flex-1 rounded-lg px-2 py-2 text-center transition ${
                              canAct
                                ? "bg-gray-900 hover:bg-black"
                                : "bg-gray-300 cursor-not-allowed"
                            }`}
                            title={!canAct ? "Complete earlier stops first" : ""}
                          >
                            <div className="text-[10px] font-semibold uppercase tracking-wide text-white/80">
                              Start trip
                            </div>
                            <div className="text-xs font-semibold text-white">
                              Tap to log
                            </div>
                          </button>
                        )}

                        {hasStartJob ? (
                          <div className="flex-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-2 text-center">
                            <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                              Job start
                            </div>
                            <div className="text-xs font-semibold text-emerald-700">
                              ✓ {addr.startJobAt}
                            </div>
                          </div>
                        ) : hasStartTrip ? (
                          <button
                            onClick={() => {
                              if (hasStartTrip && !hasStartJob && canAct) {
                                handleTimestamp(runKey, idx, "startJobAt");
                                if (addr.jobId)
                                  updateJob(addr.jobId, {
                                    status: "arrived",
                                    arrivedAt: Date.now(),
                                  });
                              }
                            }}
                            disabled={!canAct}
                            className={`flex-1 rounded-lg px-2 py-2 text-center transition ${
                              canAct
                                ? "bg-gray-800 hover:bg-gray-900"
                                : "bg-gray-300 cursor-not-allowed"
                            }`}
                          >
                            <div className="text-[10px] font-semibold uppercase tracking-wide text-white/80">
                              Job start
                            </div>
                            <div className="text-xs font-semibold text-white">
                              Tap to log
                            </div>
                          </button>
                        ) : (
                          <div className="flex-1 rounded-lg border border-gray-200 bg-gray-100 px-2 py-2 text-center">
                            <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                              Job start
                            </div>
                            <div className="text-xs font-semibold text-gray-400">—</div>
                          </div>
                        )}

                        {hasFinished ? (
                          <div className="flex-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-2 text-center">
                            <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                              Finished
                            </div>
                            <div className="text-xs font-semibold text-emerald-700">
                              ✓ {addr.completedTime}
                            </div>
                          </div>
                        ) : hasStartJob ? (
                          <button
                            onClick={() => {
                              if (hasStartJob && !hasFinished && canAct) {
                                handleTimestamp(runKey, idx, "completedTime");
                                if (addr.jobId)
                                  updateJob(addr.jobId, {
                                    status: "completed",
                                    completedAt: Date.now(),
                                  });
                              }
                            }}
                            disabled={!canAct}
                            className={`flex-1 rounded-lg px-2 py-2 text-center transition ${
                              canAct
                                ? "bg-emerald-600 hover:bg-emerald-700"
                                : "bg-gray-300 cursor-not-allowed"
                            }`}
                          >
                            <div className="text-[10px] font-semibold uppercase tracking-wide text-white/80">
                              Finish job
                            </div>
                            <div className="text-xs font-semibold text-white">
                              Tap to log
                            </div>
                          </button>
                        ) : (
                          <div className="flex-1 rounded-lg border border-gray-200 bg-gray-100 px-2 py-2 text-center">
                            <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                              Finish job
                            </div>
                            <div className="text-xs font-semibold text-gray-400">—</div>
                          </div>
                        )}
                      </div>
                      {addr.duration && (
                        <div className="mt-1.5 text-xs font-semibold text-emerald-700">
                          Duration: {addr.duration}
                        </div>
                      )}
                    </div>

                    {!hasFinished && (
                      <div className="px-3 sm:px-4 pb-3 sm:pb-4 flex flex-wrap justify-end gap-2">
                        {isSkipped ? (
                          <button
                            onClick={() => undoSkip(runKey, idx, addr)}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium border border-amber-300 text-amber-700 hover:bg-amber-50 transition"
                          >
                            {isFutile ? "Undo FUTILE" : "Undo skip"}
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() => {
                                setSkipTarget({ runKey, idx, addr });
                                setSkipOpen(true);
                              }}
                              disabled={!prevComplete}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${
                                prevComplete
                                  ? "border-gray-300 text-gray-600 hover:bg-gray-50"
                                  : "border-gray-200 text-gray-400 cursor-not-allowed"
                              }`}
                              title={
                                prevComplete
                                  ? "Skip this stop"
                                  : "You can only skip the current stop"
                              }
                            >
                              Skip stop
                            </button>
                            <button
                              onClick={() => {
                                setFutileTarget({ runKey, idx, addr });
                                setFutileOpen(true);
                              }}
                              disabled={!prevComplete}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${
                                prevComplete
                                  ? "border-red-300 text-red-700 hover:bg-red-50"
                                  : "border-gray-200 text-gray-400 cursor-not-allowed"
                              }`}
                              title={
                                prevComplete
                                  ? `Mark this stop as FUTILE ($${FUTILE_CHARGE_AMOUNT})`
                                  : "You can only futile the current stop"
                              }
                            >
                              FUTILE
                            </button>
                          </>
                        )}
                      </div>
                    )}

                    <div className="px-3 sm:px-4 pb-2">
                      <PhotoUploadBase64
                        jobId={addr.jobId}
                        assignmentDocId={
                          runDocMap?.[runKey] ||
                          assignmentDocId ||
                          `${user.uid}_${date}`
                        }
                        runKey={runKey}
                        index={idx}
                        existingPhotos={addr.proofPhotos || []}
                        onUpdate={refresh}
                      />
                    </div>

                    <div className="px-3 sm:px-4 pb-4">
                      {addr.jobId ? (
                        <InlineJobNotes jobId={addr.jobId} />
                      ) : (
                        <div className="text-xs text-gray-500">
                          Notes will be available once a job is created for this
                          stop.
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {docketIndex >= 0 && docketAddr && (
                <RunDocketSection
                  driverId={user.uid}
                  date={date}
                  companyId={isMultiCompanyDay ? runCompanyId : companyId}
                  runKey={runKey}
                  docketIndex={docketIndex}
                  existingPhotos={docketAddr.proofPhotos || []}
                  refresh={refresh}
                  assignmentDocId={assignmentDocId}
                  runDocMap={runDocMap}
                />
              )}
            </div>
          </section>
        );
      })}

      <DocketSignModal
        open={!!activeDocketJob}
        jobId={activeDocketJob?.jobId}
        docket={
          activeDocketJob?.documents?.[activeDocketJob?.documentIndex || 0] ||
          activeDocketJob?.docket
        }
        documents={activeDocketJob?.documents || []}
        documentIndex={activeDocketJob?.documentIndex || 0}
        documentCount={activeDocketJob?.documents?.length || 1}
        onDocumentChange={(nextIndex) =>
          setActiveDocketJob((prev) =>
            prev ? { ...prev, documentIndex: nextIndex } : prev,
          )
        }
        driverEmail={user?.email || ""}
        onClose={() => setActiveDocketJob(null)}
        onSigned={(signed) => {
          if (activeDocketJob?.jobId) {
            const documentId = signed?.documentId || "legacy";
            const currentSignedDocuments =
              jobMeta[activeDocketJob.jobId]?.signedJobDocuments || {};
            updateJobMeta(activeDocketJob.jobId, {
              signedDocketUrl: signed?.url || "",
              signedDocketStoragePath: signed?.storagePath || "",
              signedDocketFileName: signed?.name || "",
              signedDocketContentType: signed?.contentType || "",
              signedJobDocuments:
                documentId === "legacy"
                  ? currentSignedDocuments
                  : {
                      ...currentSignedDocuments,
                      [documentId]: signed,
                    },
            });
          }
          refresh?.();
        }}
      />

      <FutileModal
        open={futileOpen}
        target={futileTarget}
        onCancel={() => {
          setFutileOpen(false);
          setFutileTarget(null);
        }}
        onConfirm={async ({ note }) => {
          if (futileTarget) {
            const { runKey, idx, addr } = futileTarget;
            await applyFutile(runKey, idx, addr, note);
          }
          setFutileOpen(false);
          setFutileTarget(null);
        }}
      />

      <SkipModal
        open={skipOpen}
        target={skipTarget}
        onCancel={() => {
          setSkipOpen(false);
          setSkipTarget(null);
        }}
        onConfirm={async ({ reason, note }) => {
          if (skipTarget) {
            const { runKey, idx, addr } = skipTarget;
            await applySkip(runKey, idx, addr, reason, note);
          }
          setSkipOpen(false);
          setSkipTarget(null);
        }}
      />
    </div>
  );
}
